// Half-day leave — the decisions the apply form has to get right.
//
// Extracted from Leaves.tsx so they can be tested without rendering a
// 2000-line page. They are guards, not authority: the server re-validates the
// session against `leave.half_day`, re-derives totalDays from it, and rejects a
// multi-date half day regardless of what the form sends. These exist so the
// employee gets told on the spot instead of via a failed request.

export interface HalfDayConfig {
  enabled: boolean;
  sessions: string[];
}

export const EMPTY_HALF_DAY: HalfDayConfig = { enabled: false, sessions: [] };

/** Is the half-day control offered at all?
 *
 * Both halves disabled is a real configuration and means "not offered" — a
 * branch that turns the rule on but permits no session has said no.
 */
export function canOfferHalfDay(config: HalfDayConfig | undefined): boolean {
  return Boolean(config?.enabled) && (config?.sessions?.length ?? 0) > 0;
}

/** The session to preselect when the box is ticked.
 *
 * One permitted half means there is nothing to choose, so choosing for them is
 * kinder than presenting a radio group of one.
 */
export function defaultSession(config: HalfDayConfig | undefined): string {
  const sessions = config?.sessions ?? [];
  return sessions.length === 1 ? sessions[0] : "";
}

export function sessionLabel(session: string): string {
  if (session === "first_half") return "First half (morning off)";
  if (session === "second_half") return "Second half (afternoon off)";
  return session;
}

export type HalfDayCheck =
  | { ok: true; totalDays: number; session?: string }
  | { ok: false; title: string; description: string };

/** Validate the half-day part of an application and give back its day count.
 *
 * `spanDays` is the inclusive length of the chosen range (1 for a single date).
 */
export function checkHalfDay(args: {
  active: boolean;
  session: string;
  spanDays: number;
}): HalfDayCheck {
  const { active, session, spanDays } = args;

  if (!active) {
    return { ok: true, totalDays: spanDays };
  }
  // Half a day is half of ONE day. Across a range it would mean "half of each
  // of these days", which is a different policy and one payroll cannot score.
  if (spanDays !== 1) {
    return {
      ok: false,
      title: "Half day needs a single date",
      description: "Set the same start and end date, or turn off half day.",
    };
  }
  if (!session) {
    return {
      ok: false,
      title: "Which half?",
      description: "Pick the half of the day you're taking off.",
    };
  }
  return { ok: true, totalDays: 0.5, session };
}
